import { Injectable } from "@angular/core";
import {
  AuthActions,
  AuthService,
  createFrom,
  EventService,
  GlobalMessageActions,
  StateWithUser,
  WindowRef
} from "@spartacus/core";
import { CustomUserEvent } from "./custom-user.event";
import {
  CUSTOM_USER_EVENT,
  LoadCustomUserPayloadEventModel,
  USER_FORM_STATUS,
  USER_FORM_TYPE,
  USER_REGISTER_TYPE
} from "./custom-user.model";
import { Observable, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { ofType } from "@ngrx/effects";
import { ActionsSubject, Store } from "@ngrx/store";
import {
  Newsletter
} from "src/app/custom/cms-components/content/custom-newsletter/model/custom-newsletter.model";
import { CustomUserActions } from "./store/actions";


@Injectable({
  providedIn: 'root',
})
export class CustomUserEventBuilder {
  protected subscriptions: Subscription = new Subscription();

  constructor(protected eventService: EventService,
              protected winRef: WindowRef,
              protected actions: ActionsSubject,
              protected authService: AuthService,
              protected store: Store<StateWithUser>) {
    this.register();
  }

  protected register() {
    this.registerLoginEvent();
    this.registerSignUpEvent();
    this.registerNewsletterEvent();
  }

  /**
   * Register a login event
   */
  protected registerLoginEvent(): void {
    this.eventService.register(CustomUserEvent, this.buildLoginSuccessEvent());
    this.eventService.register(CustomUserEvent, this.buildLoginFailEvent());
  }

  /**
   * Register a Signup event
   */
  protected registerSignUpEvent(): void {
    this.eventService.register(CustomUserEvent, this.buildSignUpEventSuccess());
    this.eventService.register(CustomUserEvent, this.buildSignUpEventFail());
  }

  protected registerNewsletterEvent(): void {
    this.eventService.register(CustomUserEvent, this.buildSignUpNewsletterEvent());
  }

  /**
   * Returns login event stream
   */
  protected buildLoginSuccessEvent(): Observable<CustomUserEvent> {
    return this.actions.pipe(
      ofType(AuthActions.LOGIN),
      map(() => {
        return createFrom(CustomUserEvent, {
          event: CUSTOM_USER_EVENT.LOGIN_OK,
          form_status: USER_FORM_STATUS.OK
        })
      })
    )
  }

  protected buildLoginFailEvent(): Observable<CustomUserEvent> {
    return this.actions.pipe(
      ofType(GlobalMessageActions.ADD_MESSAGE),
      filter(globalMessage => (globalMessage as GlobalMessageActions.AddMessage).payload.text.key === "httpHandlers.badRequest.bad_credentials"),
      map(() =>
        createFrom(CustomUserEvent, {
          event: CUSTOM_USER_EVENT.LOGIN_FAIL,
          form_status: USER_FORM_STATUS.FAIL
        }))
    )
  }


  /**
   * Returns sign up event stream
   */
  protected buildSignUpEventSuccess(): Observable<CustomUserEvent> {
    return this.actions.pipe(
      ofType(CustomUserActions.REGISTERED_USER_SUCCESS),
      map((customUserPayload: LoadCustomUserPayloadEventModel) => {
        const registerFormPayload = customUserPayload.payload.payload
        return createFrom(CustomUserEvent, {
          event: CUSTOM_USER_EVENT.REGISTRO_OK,
          form_status: USER_FORM_STATUS.OK,
          form_type: USER_FORM_TYPE.REGISTRO,
          register_type: this.getUserRegisterType(registerFormPayload)
        })
      })
    )
  }

  /**
   * Returns sign up fail event stream
   */
  protected buildSignUpEventFail(): Observable<CustomUserEvent> {
    return this.actions.pipe(
      ofType(CustomUserActions.REGISTERED_USER_FAIL),
      map((customUserPayload: LoadCustomUserPayloadEventModel) => {
        const registerFormPayload = customUserPayload.payload.payload
        return createFrom(CustomUserEvent, {
          event: CUSTOM_USER_EVENT.REGISTRO_FAIL,
          form_status: USER_FORM_STATUS.FAIL,
          form_type: USER_FORM_TYPE.REGISTRO,
          register_type: this.getUserRegisterType(registerFormPayload)
        })
      })
    )
  }

  protected buildSignUpNewsletterEvent(): Observable<CustomUserEvent> {
    return this.actions.pipe(
      ofType(CustomUserActions.REGISTERED_NEWSLETTER_SUCCESS),
      map((customUserPayload: LoadCustomUserPayloadEventModel) => {
        const newsletter: Newsletter = customUserPayload.payload.payload
        return createFrom(CustomUserEvent, {
          event: CUSTOM_USER_EVENT.NEWSLETTER,
          form_status: USER_FORM_STATUS.OK,
          form_type: USER_FORM_TYPE.NEWSLETTER,
          register_type: this.getNewsletterRegisterType(newsletter)
        })
      })
    )
  }

  private getUserRegisterType(payload): USER_REGISTER_TYPE {
    const isProfessional = payload?.professional ?? undefined;
    if (isProfessional == undefined) {
      return USER_REGISTER_TYPE.NONE
    }
    return isProfessional ? USER_REGISTER_TYPE.EMPRESA : USER_REGISTER_TYPE.PARTICULAR;
  }

  private getNewsletterRegisterType(newsletter: Newsletter): USER_REGISTER_TYPE {
    const company = newsletter.company ?? false;
    const particular = newsletter.particular ?? false;
    if (company === true && particular === false) {
      return USER_REGISTER_TYPE.EMPRESA;
    } else if (company === false && particular === true) {
      return USER_REGISTER_TYPE.PARTICULAR;
    }
    return USER_REGISTER_TYPE.NONE;
  }
}
